import * as React from 'react';
import moment from 'moment';
import { EventInvitationMessage } from 'Src/models/message';
import { SchoolEvent } from 'Src/models/event';
import { getParentConsentEventName } from './getParentConsentEventName';

export type ParentConsentItem = {
  consentRequest: EventInvitationMessage;
  childEvent: SchoolEvent;
};

interface Props {
  items: ParentConsentItem[];
  childIdList: string[];
  readOnly?: boolean;
  onRespond?: (item: ParentConsentItem) => void;
  onView?: (item: ParentConsentItem) => void;
  onEdit?: (item: ParentConsentItem) => void;
  onWithdraw?: (item: ParentConsentItem) => void;
  onGiveConsentInstead?: (item: ParentConsentItem) => void;
}

const statusOf = (item: ParentConsentItem) =>
  String(item.consentRequest.invitationStatus || 'NOT_READY').toUpperCase();

const isPending = (item: ParentConsentItem) => !['ACCEPTED', 'REJECTED'].includes(statusOf(item));

const canChangeResponse = (item: ParentConsentItem) => {
  const boundary = item.consentRequest.deadlineForAnswers || item.childEvent.startTime;
  return !!boundary && moment().isBefore(moment(boundary));
};

const groupByEvent = (items: ParentConsentItem[]) =>
  items.reduce((groups, item) => {
    const key = String(item.childEvent.id || item.consentRequest.eventId || item.consentRequest.threadId);
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
    return groups;
  }, {} as Record<string, ParentConsentItem[]>);

export const ParentConsentEventList: React.FC<Props> = props => {
  const renderGroups = (items: ParentConsentItem[], pendingSection: boolean) => {
    const groups = groupByEvent(items);
    return Object.keys(groups)
      .sort((left, right) =>
        moment(groups[left][0].childEvent.startTime).diff(moment(groups[right][0].childEvent.startTime))
      )
      .map(key => {
        const group = groups[key];
        const event = group[0].childEvent;
        const start = moment(event.startTime);
        const deadline = group.map(item => item.consentRequest.deadlineForAnswers).find(Boolean) || event.startTime;
        const eventName = getParentConsentEventName(event, props.childIdList, group[0].consentRequest);

        return (
          <article className={pendingSection ? 'parentConsentEvent' : 'parentConsentEvent is-responded'} key={key}>
            <header>
              <time>
                <b>{start.format('ddd').toUpperCase()}</b>
                <strong>{start.format('D')}</strong>
                <b>{start.format('MMM').toUpperCase()}</b>
              </time>
              <div>
                <h3>{eventName}</h3>
                <p>
                  {start.format('ddd D MMM YYYY')} · {start.format('HH:mm')}
                  {event.endTime ? ` – ${moment(event.endTime).format('HH:mm')}` : ''}
                </p>
              </div>
              {pendingSection && !props.readOnly ? (
                <span>
                  Reply by<strong>{moment(deadline).format('ddd D MMM, HH:mm')}</strong>
                </span>
              ) : null}
            </header>
            {group.map(item => {
              const request = item.consentRequest;
              const status = statusOf(item);
              const pending = isPending(item);
              const accepted = status === 'ACCEPTED';
              const name = [request.playerDetailsData?.firstName, request.playerDetailsData?.lastName]
                .filter(Boolean)
                .join(' ');
              const initials = name
                .split(' ')
                .map(value => value[0])
                .slice(0, 2)
                .join('');
              const responded = request.actionPerformedBySetAt || request.invitationStatusSetAt;
              const canChange = !props.readOnly && canChangeResponse(item);
              const subline = props.readOnly && pending
                ? 'The deadline passed without a response'
                : pending
                ? 'Not answered yet'
                : `${accepted ? 'Given' : 'Declined'}${responded ? ` · ${moment(responded).format('D MMM, HH:mm')}` : ''}`;

              return (
                <div className="parentConsentEvent__child" key={request.id}>
                  <i>{initials}</i>
                  <div>
                    <strong>{name}</strong>
                    <em className={accepted ? 'is-accepted' : pending ? '' : 'is-declined'}>
                      {props.readOnly && pending
                        ? 'No response'
                        : pending
                        ? 'Needs your response'
                        : accepted
                        ? 'Consent given'
                        : 'Declined'}
                    </em>
                    <small>{subline}</small>
                    {request.updatedByStaff ? <small>Updated by staff{request.staffUpdatedAt ? ` · ${moment(request.staffUpdatedAt).format('D MMM, HH:mm')}` : ''}</small> : null}
                  </div>
                  <span className="parentConsentEvent__actions">
                    {pending && canChange ? (
                      <>
                        <button
                          type="button"
                          className="is-secondary is-danger"
                          onClick={() => props.onWithdraw && props.onWithdraw(item)}
                        >
                          Decline
                        </button>
                        <button type="button" onClick={() => props.onRespond && props.onRespond(item)}>
                          Give consent
                        </button>
                      </>
                    ) : !pending ? (
                      <>
                        <button type="button" className="is-secondary" onClick={() => props.onView && props.onView(item)}>
                          View answers
                        </button>
                        {canChange && accepted ? (
                          <>
                            <button type="button" className="is-secondary" onClick={() => props.onEdit && props.onEdit(item)}>
                              Edit answers
                            </button>
                            <button type="button" className="is-secondary is-danger" onClick={() => props.onWithdraw && props.onWithdraw(item)}>
                              Withdraw consent
                            </button>
                          </>
                        ) : null}
                        {canChange && !accepted ? (
                          <button type="button" onClick={() => props.onGiveConsentInstead && props.onGiveConsentInstead(item)}>
                            Give consent instead
                          </button>
                        ) : null}
                      </>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </article>
        );
      });
  };

  if (props.readOnly) return <div className="parentConsentEvents">{renderGroups(props.items, false)}</div>;

  const pending = props.items.filter(isPending);
  const responded = props.items.filter(item => !isPending(item));
  const pendingEvents = Object.keys(groupByEvent(pending)).length;

  return (
    <>
      <section className="parentConsentSection">
        <header>
          <h2>Needs your response</h2>
          <span>{pending.length} for {pendingEvents} event{pendingEvents === 1 ? '' : 's'}</span>
        </header>
        {pending.length ? (
          <div className="parentConsentEvents">{renderGroups(pending, true)}</div>
        ) : (
          <div className="parentConsentAllCaughtUp">✓ You’re all caught up. No consent requests need a response.</div>
        )}
      </section>
      <section className="parentConsentSection">
        <header>
          <h2>Responded</h2>
          <span>You can change a response until the deadline</span>
        </header>
        {responded.length ? (
          <div className="parentConsentEvents">{renderGroups(responded, false)}</div>
        ) : (
          <div className="parentListEmpty"><strong>No responses yet</strong></div>
        )}
      </section>
    </>
  );
};

ParentConsentEventList.displayName = 'ParentConsentEventList';
